import {
  Equal,
  IQueryCommandOptions,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import { ITourUserHasBuildings } from '@llws/lift-entity-interfaces';
import {
  decodeHtml,
  ISelectOption,
  uniqueMap,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { adminRequest } from '@/shared/request-configs/admin-request';
import vm from '@/main';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';

interface ITourUsersHasBuildingsState {
  propertiesOptions: Array<ISelectOption>,
  citiesOptions: Array<ISelectOption>,
  tourUserHasBuildings: Array<ITourUserHasBuildings>,
  tourUserHasBuildingsById: Array<ITourUserHasBuildings>,
  agentsOptionsForProperty: Array<ISelectOption>,
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITourUserHasBuildings>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

const propertiesAndCitiesOptionsQueryString: IQueryCommandOptions = {
  relations: [
    {
      path: 'building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'c',
    },
    {
      path: 'b.tourBuildingSettings',
      alias: 'tbs',
    },
  ],
  select: [
    'id',
    'buildingId',
    'userId',
    'b.buildingName',
    'b.clientId',
    'c.cityName',
    'tbs.status',
  ],
  limit: 9999,
};

const agentsOptionsForBuildingsQueryString: IQueryCommandOptions = {
  relations: [
    {
      path: 'user',
      alias: 'u',
    },
    {
      path: 'u.tourUserSettings',
      alias: 'tus',
    },
    {
      path: 'building',
      alias: 'b',
    },
  ],
  select: [
    'id',
    'buildingId',
    'userId',
    'tus.clientId',
    'tus.status',
  ],
  limit: 9999,
};

const tourUserHasBuildingsFilters: IQueryCommandOptions = {
  relations: [
    {
      alias: 'user',
      path: 'user',
    },
    {
      alias: 'building',
      path: 'building',
    },
    {
      alias: 'city',
      path: 'building.city',
    },
  ],
  limit: 9999,
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    propertiesOptions: [] as Array<ISelectOption>,
    citiesOptions: [] as Array<ISelectOption>,
    tourUserHasBuildings: [] as Array<ITourUserHasBuildings>,
    tourUserHasBuildingsFilters: [] as Array<ITourUserHasBuildings>,
    agentsOptionsForProperty: [] as Array<ISelectOption>,
  },

  getters: {
    ...base.getters,
    propertiesOptions(state: ITourUsersHasBuildingsState): ISelectOption[] {
      return state.propertiesOptions;
    },
    citiesOptions(state: ITourUsersHasBuildingsState): ISelectOption[] {
      return state.citiesOptions;
    },
    tourUserHasBuildings(state: ITourUsersHasBuildingsState): ITourUserHasBuildings[] {
      return state.tourUserHasBuildings || [];
    },
    tourUserHasBuildingsById(state: ITourUsersHasBuildingsState): ITourUserHasBuildings[] {
      return state.tourUserHasBuildingsById;
    },
    agentsOptionsForProperty(state: ITourUsersHasBuildingsState): ISelectOption[] {
      return state.agentsOptionsForProperty;
    },
  },

  mutations: {
    ...base.mutations,

    setPropertiesAndCitiesOptions: (state: ITourUsersHasBuildingsState, payload: Array<ITourUserHasBuildings>) => {
      state.propertiesOptions = uniqueMap(payload, (tour) => tour.buildingId, (tour) => ({
        text: decodeHtml(tour.building?.buildingName),
        value: tour.buildingId,
      }));

      state.citiesOptions = uniqueMap(payload, (tour) => tour.buildingId, (tour) => ({
        text: decodeHtml(tour.building?.city?.cityName),
        value: tour.building?.city?.id,
      }));
    },
    setTourUserHasBuildings(state: ITourUsersHasBuildingsState, payload: Array<ITourUserHasBuildings>) {
      state.tourUserHasBuildings = payload;
    },
    setTourUserHasBuildingsById(state: ITourUsersHasBuildingsState, payload: Array<ITourUserHasBuildings>) {
      state.tourUserHasBuildingsById = payload;
    },
    setAgentsOptionsForProperty: (state: ITourUsersHasBuildingsState, payload: Array<ITourUserHasBuildings>) => {
      const options: ISelectOption[] = payload.map((tourUser) => ({
        text: `${tourUser.user!.firstName} ${tourUser.user!.lastName}`,
        value: tourUser.userId,
      })) || [];
      state.agentsOptionsForProperty = options;
    },

  },

  actions: {
    ...base.actions,

    getTourUserHasBuildings: async (ctx: IAction<IStateStrict<ITourUserHasBuildings>>, payload: IFilterPayloadV2) => {
      const queryString = payload.filters ? { ...tourUserHasBuildingsFilters, ...payload.filters } : tourUserHasBuildingsFilters;
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_users_has_buildings${convertQueryCommandToQueryString(queryString)}`);
      ctx.commit('setTourUserHasBuildings', response.data);
    },
    getTourUserHasBuildingsById: async (ctx: IAction<IStateStrict<ITourUserHasBuildings>>, id: number) => {
      const filters: IQueryCommandOptions = {
        ...tourUserHasBuildingsFilters,
        where: [
          Equal('userId', id),
        ],
      };
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_users_has_buildings${convertQueryCommandToQueryString(filters)}`);
      ctx.commit('setTourUserHasBuildingsById', response.data);
      return response.data;
    },
    getPropertiesAndCitiesOptions: async (ctx: IAction<IStateStrict<ITourUserHasBuildings>>, payload: IQueryCommandOptions) => {
      const queryString = payload ? { ...propertiesAndCitiesOptionsQueryString, ...payload } : propertiesAndCitiesOptionsQueryString;
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted()
        ? agentGatewayResourcePathAgent
        : adminGatewayResourcePathAdmin}/tour_users_has_buildings${convertQueryCommandToQueryString(queryString)}`);
      ctx.commit('setPropertiesAndCitiesOptions', response.data);
    },
    getAgentsOptionsForBuildings: async (ctx: IAction<IStateStrict<ITourUserHasBuildings>>, payload: IFilterPayloadV2) => {
      const queryString = payload.filters ? {
        ...agentsOptionsForBuildingsQueryString,
        ...payload.filters,
      } : agentsOptionsForBuildingsQueryString;
      const url = vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin;
      const response = await adminRequest.get(`${url}/tour_users_has_buildings${convertQueryCommandToQueryString(queryString)}&propertyId=${payload.additional?.propertyId || ''}`);
      ctx.commit('setAgentsOptionsForProperty', response.data);
    },
    /**
     * Bulk update tour user building records
     * @param _ctx
     * @param payload - Object containing arrays of records to create, update, and delete
     */
    bulkUpdatePerPropertyAvailability: async (
      _ctx: IAction<IStateStrict<ITourUserHasBuildings>>,
      payload: {
        recordsToCreate: Partial<ITourUserHasBuildings>[],
        recordsToUpdate?: Partial<ITourUserHasBuildings>[],
        recordsToDelete?: Partial<ITourUserHasBuildings>[],
      },
    ) => {
      const url = vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin;

      const promises: Promise<any>[] = [];

      // Create new records
      payload.recordsToCreate.forEach((record) => {
        promises.push(adminRequest.post(`${url}/tour_users_has_buildings`, record));
      });

      // Update existing records (if provided)
      if (payload.recordsToUpdate?.length) {
        payload.recordsToUpdate.forEach((record) => {
          promises.push(adminRequest.put(`${url}/tour_users_has_buildings/${record.id}`, record));
        });
      }

      // Delete records (if provided)
      if (payload.recordsToDelete?.length) {
        const idsToDelete = payload.recordsToDelete.map((record) => record.id!);
        promises.push(adminRequest.delete(`${url}/tour_users_has_buildings`, idsToDelete));
      }

      await Promise.all(promises);
    },
  },
};
